import { AsyncPipe } from '@angular/common'
import { ChangeDetectionStrategy, Component, Input, OnInit, inject } from '@angular/core'
import { MatIconModule } from '@angular/material/icon'
import {
    getEnabledPublicDocsSections,
    getPublicDocsSectionConfig,
} from '@app/pages/public-docs/public-docs-section.config'
import { PublicDocsSection } from '@app/pages/public-docs/public-docs.models'
import { ConfirmationDialogService } from '@components-library/services/confirmation-dialog.service'
import { CommonDialogResultStatus } from '@components-library/tb-confirmation-popup/tb-confirmation-dialog.component'
import { TbDividerComponent } from '@components-library/tb-divider/tb-divider.component'
import { TbOptionComponent } from '@components-library/tb-select/components/tb-option/tb-option.component'
import { TbSelectComponent } from '@components-library/tb-select/components/tb-select/tb-select.component'
import { User } from '@core/models'
import { TranslocoModule } from '@jsverse/transloco'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'
import {
    devStorageInstance,
    DevStorageService,
    EnvironmentMode,
    SolutionType,
} from '@services/local-storage/dev-storage.service'
import { NavigatorService } from '@services/navigator.service'
import { ApplicationFacadeService } from '@services/store-facade'
import { WsProcessingService } from '@services/ws-processing.service'
import { getEnvModeFromFile, setEnvMode } from '@test/dev-utils'

import { VersionComponent } from 'src/app/feature/user-settings/version/version.component'
import { ModalLayoutComponent } from '@components-library/tb-modal-manager/modal-layout/modal-layout.component'
import { ModalContainerTypes } from '@components-library/tb-modal-manager/modal-manager.service'
import { TbMenuListItemComponent } from '@components-library/tb-menu-list-item/tb-menu-list-item.component'

interface DevPublicDocsMenuItem {
    section: PublicDocsSection
    label: string
    icon: string
}

const PUBLIC_DOCS_MENU_ICONS: Record<PublicDocsSection, string> = {
    'wiki': 'menu_book_rounded',
    'issues': 'bug_report_rounded',
    'test-cases': 'fact_check_rounded',
}

@UntilDestroy()
@Component({
    selector: 'app-user-settings-content',
    templateUrl: './user-settings-content.component.html',
    styleUrls: ['./user-settings-content.component.sass'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        TranslocoModule,
        MatIconModule,
        VersionComponent,
        TbSelectComponent,
        TbOptionComponent,
        AsyncPipe,
        TbDividerComponent,
        ModalLayoutComponent,
        TbMenuListItemComponent,
    ],
})
export class UserSettingsContentComponent implements OnInit {
    private readonly applicationFacadeService = inject(ApplicationFacadeService)
    private readonly navigatorService = inject(NavigatorService)
    private readonly wsProcessingService = inject(WsProcessingService)
    private readonly devStorageService = inject(DevStorageService)
    private readonly confirmationDialogService = inject(ConfirmationDialogService)

    @Input()
    userInfo!: User
    @Input()
    currentUserIsAdmin!: number | undefined
    @Input()
    containerLayout!: ModalContainerTypes
    @Input()
    close!: () => void

    solutionName!: string
    solutionIcon!: string

    devSolutionTypes: SolutionType[] = ['TM', 'CRM', 'QA']
    devDefaultSolutionValue: SolutionType = this.devStorageService.getSolutionType() ?? 'CRM'

    devEnvModesOptions: EnvironmentMode[] = ['dev', 'prod']
    devCurrentEnvMode$ = devStorageInstance.envMode$
    devPublicDocsMenuItems: DevPublicDocsMenuItem[] = getEnabledPublicDocsSections().map(
        (sectionName) => ({
            section: sectionName,
            label: getPublicDocsSectionConfig(sectionName).label,
            icon: PUBLIC_DOCS_MENU_ICONS[sectionName],
        }),
    )

    ngOnInit() {
        this.applicationFacadeService.selectSolution$
            .pipe(untilDestroyed(this))
            .subscribe((solution) => {
                this.solutionName = solution.name
                this.solutionIcon = this.solutionName.trimStart()[0]
            })
    }

    navigateNotifications() {
        this.navigatorService.goToNotificationPage()
    }

    logout() {
        this.navigatorService.goToRoot()
        this.wsProcessingService.logout()
    }

    resetSolution() {
        this.confirmationDialogService
            .openCommon({
                translations: {
                    title: 'user_settings.reset_solution_confirmation_title',
                    message: 'user_settings.reset_solution_confirmation_message',
                    cancel: 'user_settings.reset_solution_cancel',
                    confirm: 'user_settings.reset_solution_confirm',
                },
            })
            .subscribe((result) => {
                if (result === CommonDialogResultStatus.CONFIRM) {
                    this.applicationFacadeService.resetSolutionToTemplate()
                }
            })
    }

    envIsMock() {
        return getEnvModeFromFile() === 'mock'
    }

    envIsDev() {
        return getEnvModeFromFile() === 'dev'
    }

    devSetEnvMode(mode: EnvironmentMode) {
        setEnvMode(mode)
        window.location.reload()
    }

    devSolutionChanged(val: string) {
        this.devStorageService.setSolutionType(val as SolutionType)
        window.location.reload()
    }

    navigateToPublicDocs(sectionName: PublicDocsSection) {
        this.close()
        this.navigatorService.goToPublicDocsSection(sectionName)
    }

    protected readonly ModalContainer = ModalContainerTypes
}
